import { addToast, Button, showDialogConfirm, Table } from '@octano/global-ui';
import { useCallback, useContext, useRef } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { SectionTitle } from '../../../../../components/text';
import TableEmptyContent from '../../../../../components/text/TableEmptyContent';
import { TranslationsKeys } from '../../../../../locales/translations';
import { requetCheckLearningResultCanBeDeleted } from '../../api';
import { useColumnsLearningUnits } from '../../hooks/useColumnsLearningUnits';
import { CourseFormContext } from '../../providers/CourseProvider';
import { CourseFormFields, CourseFormMode } from '../../types';
import ModalAddCourseLearningUnit, {
  ModalAddCourseLearningUnitRef,
} from '../modals/ModalAddCourseLearningUnit';

const LearningUnitSection = () => {
  const { t } = useTranslation(TranslationsKeys.ACADEMIC_OFFER_COURSES_UPDATE);
  const CourseLearningModalRef = useRef<ModalAddCourseLearningUnitRef>(null);

  const { control, watch } = useFormContext<CourseFormFields>();
  const { mode, courseId } = useContext(CourseFormContext);
  const learningUnits = watch('learningUnits');
  const { remove, append } = useFieldArray({
    control,
    name: 'learningUnits',
  });

  const onDelete = useCallback(
    async (index: number) => {
      if (mode === CourseFormMode.WATCH) return;
      const learningUnitId = learningUnits[index]?.id;

      if (learningUnitId && mode === CourseFormMode.UPDATE) {
        const { error } = await requetCheckLearningResultCanBeDeleted(
          courseId!,
          learningUnitId,
        );
        if (error) {
          addToast({
            icon: 'error',
            color: 'danger',
            text: t(`learningUnit.modalRemove.error`),
          });
          return;
        }
      }

      showDialogConfirm({
        icon: {
          name: 'warning',
          color: 'warning',
        },
        title: t(`learningUnit.modalRemove.title`),
        subtitle: '',
        btnConfirm: {
          text: t(`learningUnit.modalRemove.removeConfirm`),
          onConfirm: () => remove(index),
        },
        btnClose: {
          text: t(`learningUnit.modalRemove.removeCancel`),
        },
      });
    },
    [remove, t, learningUnits, mode, courseId],
  );

  const onEdit = useCallback((index: number) => {
    CourseLearningModalRef?.current?.openModal(index);
  }, []);

  const columns = useColumnsLearningUnits({
    onDelete,
    onEdit,
  });

  return (
    <>
      <Row>
        <Col
          xs={12}
          className="d-flex justify-content-between align-items-center p-4"
        >
          <SectionTitle text={t(`learningUnit.title`)} />
          {mode !== CourseFormMode.WATCH && (
            <Button
              icon="plus"
              onClick={() => CourseLearningModalRef?.current?.openModal()}
              size="md"
              text={t(`learningUnit.btn.add`)}
            />
          )}
        </Col>
        <Col xs={12}>
          <Table
            columns={columns}
            data={learningUnits ?? []}
            noResultsText={
              <TableEmptyContent
                title={t(`learningUnit.table.empty.title`)}
                subtitle={t(
                  `${
                    mode === CourseFormMode.WATCH
                      ? 'learningUnit.table.empty.watchBody'
                      : 'learningUnit.table.empty.body'
                  }`,
                )}
              />
            }
          />
        </Col>
      </Row>
      <ModalAddCourseLearningUnit
        ref={CourseLearningModalRef}
        onConfirm={append}
        onDecline={() => null}
      />
    </>
  );
};

export default LearningUnitSection;
